import { useCurrentUser } from '@/hooks/data/user';
import { trackEvent } from '@/utils/tools/segment/tracking';
import { Alert, Card, Flex, Skeleton, Spin } from 'antd';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import PlanSelection from '../PlanSelection/PlanSelection';
import PaddleCheckout from '../paddle/PaddleCheckout';
import styles from './CheckoutSection.module.css';

function Row({ label, value, loading, bold }) {
   return (
      <Flex
         justify="space-between"
         align="center"
         className={bold ? styles.totalRow : styles.row}
         style={{ padding: '4px 8px' }}
      >
         <span style={{ fontWeight: bold ? 600 : 400 }}>{label}</span>
         {loading ? <Skeleton.Input active size="small" style={{ width: 60 }} /> : <span>{value}</span>}
      </Flex>
   );
}

export default function CheckoutSection(props) {
   const { plans } = props;
   const { user, error, isLoading } = useCurrentUser();
   const router = useRouter();
   const { planId } = router.query;
   const urlPlan = plans ? plans.find((plan) => plan.paddleId == planId) : null;

   const [selectedPlan, setSelectedPlan] = useState(urlPlan || plans[0]);
   const [tax, setTax] = useState(0);
   const [total, setTotal] = useState(0);
   const [unitPrice, setUnitPrice] = useState(0);
   const [checkout, setCheckout] = useState(null);
   const [paddleLoading, setPaddleLoading] = useState(true);

   useEffect(() => {
      setPaddleLoading(true);
   }, [selectedPlan]);

   async function updateCheckoutData(data) {
      setTotal(data.eventData.checkout.prices.customer.total);
      setTax(data.eventData.checkout.prices.customer.total_tax);
      setUnitPrice(data.eventData.checkout.prices.customer.unit);
      const checkoutId = data.eventData?.checkout?.id;
      const event = data.event;
      setCheckout(data);

      const trackEventData = {
         currency: 'USD',
         value: total,
         coupon: data.eventData?.checkout?.coupon?.coupon_code,
         items: [
            {
               item_id: selectedPlan.paddleId,
               item_name: selectedPlan.name,
               coupon: data.eventData?.checkout?.coupon?.coupon_code,
               discount: data.eventData?.checkout?.coupon?.discount,
               price: unitPrice,
               quantity: 1,
            },
         ],
      };
      if (event === 'Checkout.Loaded') {
         setPaddleLoading(false);
         trackEvent('begin_checkout', trackEventData);
      }

      if (event === 'Checkout.Payment.Selected') {
         trackEvent('add_payment_info', trackEventData);
      }

      if (checkoutId && event === 'Checkout.Complete') {
         try {
            const res = await fetch(`/api/paddle/checkout_complete`, {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({ checkoutId }),
            });
            const data = await res.json();
            if (data.error) {
               throw new Error(data.error);
            }

            trackEvent('purchase', trackEventData);

            router.push('/dashboard');
         } catch (err) {
            //sentry
         }
      }
   }

   if (!user) return null;

   if (!plans) return null;

   return (
      <Flex
         vertical
         gap={32}
         justify="center"
         align="center"
         style={{ width: '100%', height: '100vh' }}
      >
         {checkout?.event == 'Checkout.Complete' && (
            <div className={styles.checkoutCompleted}>
               <h1>Thank you for susbcribe to Premast</h1>
               <p> You have successfully subscribed to {selectedPlan.name} plan.</p>
               <Spin />
               <Alert type="success" message="You will redirect to dashboard in a few seconds." showIcon />
            </div>
         )}

         {checkout?.event != 'Checkout.Complete' && (
            <div className={styles.checkoutSection}>
               <div className={styles.paddleCheckout}>
                  <PaddleCheckout user={user} selectedPlan={selectedPlan} onChange={updateCheckoutData} />
               </div>
               <div className={styles.checkoutDetails}>
                  <h1>Order Summary</h1>
                  <PlanSelection plans={plans} onChange={setSelectedPlan} selectedPlan={selectedPlan} />
                  <Flex vertical gap={32} style={{ width: '100%' }}>
                     <Card variant="outlined" styles={{ body: { padding: 8 } }}>
                        <Row label={selectedPlan.name} value={`$ ${unitPrice}`} loading={paddleLoading} />
                        <Row label="Subtotal" value={`$ ${(total - tax).toFixed(2)}`} loading={paddleLoading} />
                        <Row label="Tax" value={`$ ${tax}`} loading={paddleLoading} />
                        <Row
                           label="Discount"
                           value={`$ ${total - unitPrice}`}
                           loading={paddleLoading}
                        />
                        <Row label="Total" value={`$ ${total}`} loading={paddleLoading} bold />
                     </Card>
                  </Flex>
               </div>
               {paddleLoading && (
                  <div
                     style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.4)',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1001,
                     }}
                  >
                     <Spin size="large" />
                  </div>
               )}
            </div>
         )}
      </Flex>
   );
}
